import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { apiService } from '../services/apiService';

const actions = ['', 'LOGIN', 'LOGOUT', 'CREATE_PROJECT', 'UPDATE_PROJECT', 'DELETE_PROJECT', 'DOWNLOAD_PROXY', 'RUN_TEST', 'RUN_TEST_CYCLE', 'ACCESS_ENVIRONMENT'];

const AdminUserActivityPage: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [actionType, setActionType] = useState('');
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setDebouncedSearch(search), 350);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [search]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiService.getAdminActivityLogs({ page: 0, size: 60, actionType: actionType || undefined, search: debouncedSearch || undefined });
      setRows(res?.content || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [actionType, debouncedSearch]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>User Activity Logs</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <TextField size="small" label="Global Search" value={search} onChange={(e) => setSearch(e.target.value)} />
        <TextField size="small" select label="Action" value={actionType} onChange={(e) => setActionType(e.target.value)}>
          {actions.map((a) => <MenuItem key={a || 'all'} value={a}>{a || 'All'}</MenuItem>)}
        </TextField>
        <Button variant="outlined" onClick={() => apiService.exportAdminActivityCsv({ actionType: actionType || undefined })}>Export CSV</Button>
      </Box>
      {loading && <CircularProgress size={22} />}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User ID</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>Device</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Timestamp</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.userId}</TableCell>
                <TableCell>{row.actionType}</TableCell>
                <TableCell>{row.entityType} {row.entityId ? `#${row.entityId}` : ''}</TableCell>
                <TableCell>{row.osName || '-'} / {row.browserName || '-'}</TableCell>
                <TableCell>{row.ipAddress || '-'}</TableCell>
                <TableCell>{row.createdAt}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default AdminUserActivityPage;
